import { useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SendIcon from '@mui/icons-material/Send';
import { selectUser } from 'app/store/userSlice';

const isAiChatEnabled = () => {
  const rawFlag = process.env.REACT_APP_AI_CHAT_ENABLED;
  if (rawFlag == null || rawFlag === '') {
    return true;
  }

  return ['1', 'true', 'yes', 'on'].includes(rawFlag.toLowerCase());
};

function AiAssistantWidget() {
  const user = useSelector(selectUser);
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(false);
  const chatEnabled = isAiChatEnabled();
  const canUseChat = user?.role === 'admin';

  if (!chatEnabled || !canUseChat) {
    return null;
  }

  const startSessionIfNeeded = async () => {
    if (sessionId) {
      return sessionId;
    }

    const response = await axios.post('/api/chat/sessions', {
      title: 'AI Assistant',
    });
    const newSessionId = response?.data?.sessionId;
    setSessionId(newSessionId);
    return newSessionId;
  };

  const handleSend = async () => {
    if (!messageText.trim() || loading) {
      return;
    }

    const userMessage = messageText.trim();
    setMessageText('');
    setLoading(true);
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);

    try {
      const activeSessionId = await startSessionIfNeeded();
      const response = await axios.post(`/api/chat/sessions/${activeSessionId}/messages`, {
        message: userMessage,
      });
      const latestMessages = response?.data?.messages || [];
      setMessages(latestMessages.map((m) => ({ role: m.role, content: m.content })));
    } catch (error) {
      const message = error?.response?.data?.message || 'Unable to reach AI assistant';
      setMessages((prev) => [...prev, { role: 'assistant', content: message }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ position: 'fixed', right: 20, bottom: 24, zIndex: 1200 }}>
      {!open ? (
        <Button
          variant="contained"
          color="primary"
          startIcon={<SmartToyIcon />}
          onClick={() => setOpen(true)}
          sx={{ borderRadius: 999, px: 2 }}
        >
          AI Assistant
        </Button>
      ) : (
        <Paper elevation={10} sx={{ width: 390, borderRadius: 3, overflow: 'hidden' }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 2, py: 1.2, bgcolor: 'primary.main', color: 'primary.contrastText' }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.dark' }}>
                <SmartToyIcon fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  AI Assistant
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Admin preview
                </Typography>
              </Box>
            </Stack>
            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'inherit' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Paper variant="outlined" sx={{ height: 320, overflowY: 'auto', p: 1.5, m: 1.5, mb: 1, borderRadius: 2 }}>
            {messages.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Ask about products, orders, and workflows.
              </Typography>
            ) : (
              messages.map((message, index) => (
                <Box
                  key={`${message.role}-${index}`}
                  mb={1}
                  sx={{
                    display: 'flex',
                    justifyContent: message.role === 'assistant' ? 'flex-start' : 'flex-end',
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: '84%',
                      px: 1.25,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: message.role === 'assistant' ? 'grey.100' : 'primary.main',
                      color: message.role === 'assistant' ? 'text.primary' : 'primary.contrastText',
                    }}
                  >
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      {message.role === 'assistant' ? 'Assistant' : 'You'}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {message.content}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
          </Paper>

          <Stack direction="row" spacing={1} sx={{ px: 1.5, pb: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              value={messageText}
              onChange={(event) => setMessageText(event.target.value)}
              placeholder="Type your message"
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  handleSend();
                }
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSend}
              disabled={loading || !messageText.trim()}
              startIcon={loading ? <CircularProgress color="inherit" size={14} /> : <SendIcon fontSize="small" />}
            >
              Send
            </Button>
          </Stack>
        </Paper>
      )}
    </Box>
  );
}

export default AiAssistantWidget;
